import { z } from 'zod';
import { optionalText, paginationQuery, requiredText } from './common';

export const INVENTORY_STATUSES = ['open', 'applied', 'canceled'] as const;
export type InventoryStatusKey = (typeof INVENTORY_STATUSES)[number];

export const INVENTORY_STATUS_LABELS: Record<InventoryStatusKey, string> = {
  open: 'Em contagem',
  applied: 'Aplicada',
  canceled: 'Cancelada',
};

/** Uma contagem fotografa até 5000 itens (produto sem grade ou cada variação ativa). */
export const INVENTORY_MAX_ITEMS = 5000;

export const inventoryCountCreateSchema = z
  .object({
    name: requiredText(80, 'Dê um nome para a contagem'),
    /** all = todos os produtos com estoque controlado; categories/products = recorte. */
    scope: z.enum(['all', 'categories', 'products']).default('all'),
    categoryIds: z.array(z.string().uuid()).max(200).default([]),
    productIds: z.array(z.string().uuid()).max(INVENTORY_MAX_ITEMS).default([]),
    notes: optionalText(500),
  })
  .refine((v) => v.scope !== 'categories' || v.categoryIds.length > 0, { message: 'Escolha ao menos uma categoria.', path: ['categoryIds'] })
  .refine((v) => v.scope !== 'products' || v.productIds.length > 0, { message: 'Escolha ao menos um produto.', path: ['productIds'] });
export type InventoryCountCreateInput = z.infer<typeof inventoryCountCreateSchema>;

export const inventoryCountUpdateSchema = z.object({
  name: requiredText(80, 'Dê um nome para a contagem').optional(),
  notes: optionalText(500),
});

export const inventoryListQuery = paginationQuery.extend({
  status: z.enum(INVENTORY_STATUSES).optional(),
  pageSize: z.coerce.number().int().min(1).max(100).default(30),
});

export const inventoryItemsQuery = paginationQuery.extend({
  /** counted = já contados; pending = falta contar; diff = contados com diferença. */
  filter: z.enum(['all', 'counted', 'pending', 'diff']).default('all'),
  /** Nome, variação, código, SKU ou código de barras. */
  q: z.string().trim().max(100).optional(),
  pageSize: z.coerce.number().int().min(1).max(500).default(100),
});
export type InventoryItemsQuery = z.infer<typeof inventoryItemsQuery>;

export const inventoryItemsPatchSchema = z.object({
  items: z
    .array(
      z.object({
        itemId: z.string().uuid(),
        /** Quantidade contada; null limpa a contagem do item (volta a "falta contar"). */
        countedQty: z.number().min(0, 'A quantidade contada não pode ser negativa').max(1_000_000).nullable(),
      }),
    )
    .min(1)
    .max(INVENTORY_MAX_ITEMS),
});
export type InventoryItemsPatchInput = z.infer<typeof inventoryItemsPatchSchema>;

export const inventoryScanSchema = z.object({
  /** Código lido: código de barras ou SKU da variação, código de barras ou código do produto. */
  barcode: z.string().trim().min(1, 'Leia ou digite o código').max(60),
  /** Quanto somar (padrão 1). Negativo desfaz uma leitura a mais. */
  qty: z
    .number()
    .min(-1000)
    .max(1000)
    .refine((v) => v !== 0, 'Informe a quantidade')
    .default(1),
});
export type InventoryScanInput = z.infer<typeof inventoryScanSchema>;
